import React from "react";
import { Modal } from "antd";
import axios from "axios";
import { toast } from "react-toastify";

const PRICE_EPS = 0.005;

/** Master list price: company product_stock.list_price, else product.sale_price */
export function getReferenceListPriceFromProduct(product) {
  if (!product) return 0;
  const ps = Array.isArray(product.product_stock) ? product.product_stock[0] : null;
  const lp = ps?.list_price;
  if (lp != null && lp !== "" && Number.isFinite(Number(lp))) return Number(lp);
  return Number(product.sale_price) || 0;
}

/** Master purchase / cost price on product record */
export function getReferencePurchasePriceFromProduct(product) {
  if (!product) return 0;
  const n = Number(product.purchase_price);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Rows where invoice line sale_price (MRP) differs from reference_list_price snapshot.
 * `sale_price` on row = price entered on invoice / purchase line (MRP).
 */
export function collectListPriceChanges(selectedProds) {
  return (selectedProds || []).filter((p) => {
    if (p.isNewProduct || !p.id) return false;
    // Require snapshot so we don't treat every line as "changed" when ref was never set
    if (p.reference_list_price === undefined || p.reference_list_price === null) return false;
    const refN = Number(p.reference_list_price);
    const cur = Number(p.sale_price);
    if (!Number.isFinite(cur) || !Number.isFinite(refN)) return false;
    return Math.abs(cur - refN) > PRICE_EPS;
  });
}

/**
 * Rows where line purchase_price differs from reference_purchase_price snapshot (master cost).
 */
export function collectPurchasePriceChanges(selectedProds) {
  return (selectedProds || []).filter((p) => {
    if (p.isNewProduct || !p.id) return false;
    if (p.reference_purchase_price === undefined || p.reference_purchase_price === null) return false;
    const refN = Number(p.reference_purchase_price);
    const cur = Number(p.purchase_price);
    if (!Number.isFinite(cur) || !Number.isFinite(refN)) return false;
    return Math.abs(cur - refN) > PRICE_EPS;
  });
}

export async function syncProductsListPrices(products) {
  const token = localStorage.getItem("access-token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  await Promise.all(
    products.map((p) =>
      axios.patch(
        `product/${p.id}/sync-list-price`,
        { list_price: Number(p.sale_price) },
        { headers }
      )
    )
  );
}

export async function syncProductsPurchasePrices(products) {
  const token = localStorage.getItem("access-token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  await Promise.all(
    products.map((p) =>
      axios.patch(
        `product/${p.id}/sync-purchase-price`,
        { purchase_price: Number(p.purchase_price) },
        { headers }
      )
    )
  );
}

/**
 * When invoice line List / MRP differs from master snapshot, prompt like purchase flow.
 * @param {Array} rows - selected line products (must have reference_list_price for existing ids)
 * @param {() => void | Promise<void>} runSubmit - save invoice / bill after user choice
 * @param {{ cancelText?: string }} [options]
 * @returns {Promise<void>}
 */
export function confirmMasterListPriceSyncThen(rows, runSubmit, options = {}) {
  const { cancelText = "No, save bill only" } = options;
  const listChanges = collectListPriceChanges(rows);
  if (listChanges.length === 0) {
    return Promise.resolve(runSubmit());
  }

  const fmtRs = (n) => `Rs.${Number(n ?? 0).toFixed(2)}`;

  return new Promise((resolve) => {
    Modal.confirm({
      title: "Update product master prices?",
      width: 580,
      content: (
        <div>
          <p style={{ marginBottom: 12 }}>
            The <strong>List / MRP</strong> on this bill differs from the saved product price for the items below.
            Update the product master list price for future bills?
          </p>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {listChanges.map((p) => {
              const prevList = Number(p.reference_list_price ?? 0);
              const curList = Number(p.sale_price ?? 0);
              return (
                <li key={`${p.id}-${p.key}`} style={{ marginBottom: 12 }}>
                  <strong>{p.name || p.isbn || `Product #${p.id}`}</strong>
                  <div style={{ marginTop: 6, color: "#333" }}>
                    <span style={{ color: "#666" }}>List / MRP — </span>
                    Previously <strong>{fmtRs(prevList)}</strong>
                    {prevList <= 0.005 && (
                      <span style={{ fontSize: 12, color: "#888" }}> (not set / zero on master)</span>
                    )}
                    <span style={{ color: "#666" }}> → On this bill </span>
                    <strong style={{ color: "#1890ff" }}>{fmtRs(curList)}</strong>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ),
      okText: "Yes, update list prices",
      okType: "primary",
      okButtonProps: {
        type: "primary",
        style: {
          background: "#1890ff",
          backgroundColor: "#1890ff",
          borderColor: "#1890ff",
          color: "#fff",
          fontWeight: 600,
        },
      },
      cancelText,
      cancelButtonProps: {
        style: {
          color: "rgba(0, 0, 0, 0.88)",
          borderColor: "#d9d9d9",
        },
      },
      onOk: async () => {
        try {
          await syncProductsListPrices(listChanges);
          toast.success("Product list prices updated.");
        } catch (err) {
          console.error(err);
          toast.error(
            err.response?.data?.message || err.message || "Failed to update list prices"
          );
          throw err;
        }
        await runSubmit();
        resolve();
      },
      onCancel: async () => {
        await runSubmit();
        resolve();
      },
    });
  });
}
